import React, { useState, useEffect } from 'react'
import { useTranslation } from '../../../hooks/useTranslation'
import { useExercise } from '../ExerciseProvider'
import { Input } from '~/lib/ui/input'
import { AylaButton } from '../Buttons'
import SavedStatusIcon from '~/components/shared/SavedStatusIcon'
import AylaResponseScrollArea from '~/components/ShortCourses/AylaResponseScrollArea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/lib/ui/select'
import AylaLoading from '~/components/shared/AylaLoading'
import AylaTimeoutError from '~/components/shared/AylaTimeout'

const UpdateYourLinkedinPageTwo = () => {
  const {
    formattedExerciseName,
    exerciseIndex,
    handleChange,
    getResponse,
    updateResponse,
    saveExercise,
    embedExercise,
    requestAylaLinkedinAbout,
  } = useExercise()
  const { t } = useTranslation(formattedExerciseName)
  const pageTwoTranslations = t('pages.1')
  const questions = pageTwoTranslations.questions
  const [submitted, setSubmitted] = useState(false)
  const [aylaResponse, setAylaResponse] = useState('')
  const [displayedText, setDisplayedText] = useState('')
  const [index, setIndex] = useState(0)
  const [typingComplete, setTypingComplete] = useState(false)
  const [loadingStatus, setLoadingStatus] = useState(null)
  const [addAchievements, setAddAchievements] = useState(
    getResponse(exerciseIndex, 'additional_skills') || null,
  )
  const showCursor = submitted && displayedText.length <= 0
  const [multiInputs, setMultiInputs] = useState(
    getResponse(1, 'additional_achievements') || [''],
  )

  const handleAylaStatement = async () => {
    setSubmitted(true)
    setLoadingStatus('loading')
    setAylaResponse('')
    setDisplayedText('')
    setIndex(0)
    setTypingComplete(false)
    if (embedExercise) {
      await saveExercise()
    }
    await getAylaData()
  }

  const getAylaData = async () => {
    try {
      const data = await requestAylaLinkedinAbout()
      setAylaResponse(data.response)
      setLoadingStatus('complete')
    } catch {
      setLoadingStatus('failed')
    }
  }

  useEffect(() => {
    if (typingComplete) {
      updateResponse(exerciseIndex, 'ayla_skills', aylaResponse)
    }
  }, [typingComplete])

  useEffect(() => {
    if (
      typeof window.initTypeahead === 'function' &&
      typeof Bloodhound === 'function'
    ) {
      window.initTypeahead()
    }
  }, [])

  const handleSelectChange = (section, slug, event) => {
    const response = event
    updateResponse(section, slug, response)
    const addAchievement = response
    setAddAchievements(addAchievement)
  }

  const handleInputChange = (section, slug, index, event) => {
    const value = event.target.value
    setMultiInputs((prev) => [
      ...prev.slice(0, index),
      value,
      ...prev.slice(index + 1),
    ])
    updateResponse(section, slug, multiInputs)
  }

  const addInputField = () => {
    setMultiInputs((prev) => [...prev, ''])
  }

  return (
    <div className="questions md:min-h-80">
      {questions.map((question, idx) => (
        <div key={idx} className="mb-4">
          <div className="flex w-full flex-col">
            {question.type === 'autocomplete' && (
              <>
                <label className="!mb-1">{question.question}</label>
                <Input
                  name="roles"
                  slug={question.slug}
                  data-source="roles"
                  onChange={(e) => {
                    handleChange(exerciseIndex, question.slug, e)
                  }}
                  className={`autocomplete mb-2 ${question.autocomplete}-typeahead`}
                  defaultValue={getResponse(exerciseIndex, question.slug) || ''}
                />
                <AylaButton
                  onClick={handleAylaStatement}
                  containerClassName="!mt-2"
                >
                  {t('start_exercise.lets_go')}
                  <SavedStatusIcon loadingStatus={loadingStatus} />
                </AylaButton>
                {loadingStatus === 'complete' && (
                  <AylaResponseScrollArea
                    showBorder={submitted}
                    showCursor={showCursor}
                    aylaResponse={aylaResponse}
                    typingComplete={typingComplete}
                    setTypingComplete={setTypingComplete}
                    displayedText={displayedText}
                    setDisplayedText={setDisplayedText}
                    index={index}
                    setIndex={setIndex}
                    className="mb-4 h-48"
                  />
                )}
                {loadingStatus === 'loading' && (
                  <div className="flex items-center justify-center">
                    <AylaLoading />
                  </div>
                )}
                {loadingStatus === 'failed' && (
                  <AylaTimeoutError onRetry={handleAylaStatement} />
                )}
              </>
            )}
            {question.type === 'select' && typingComplete && (
              <>
                <label className="!mb-1">{question.question}</label>
                <Select
                  onValueChange={(e) =>
                    handleSelectChange(exerciseIndex, question.slug, e)
                  }
                  value={getResponse(exerciseIndex, question.slug) || ''}
                  className="w-full rounded-[20px] border border-[#D3D3D3] bg-[#F7FAFC] p-3 font-roboto text-base"
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('select_an_option')} />
                  </SelectTrigger>
                  <SelectContent>
                    {question.choices.map((choice, index) => (
                      <SelectItem key={index} value={choice}>
                        {choice}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}
          </div>
        </div>
      ))}
      {addAchievements === 'Yes' && typingComplete && (
        <>
          <p>{pageTwoTranslations.results.good_result}</p>
          <label className="!mb-1">{pageTwoTranslations.achievements}</label>
          {multiInputs.map((value, index) => (
            <Input
              key={index}
              slug={`additional_achievements`}
              className="mb-2"
              onChange={(e) =>
                handleInputChange(
                  exerciseIndex,
                  `additional_achievements`,
                  index,
                  e,
                )
              }
              defaultValue={value || ''}
            />
          ))}
          <button type="button" onClick={() => addInputField()}>
            {t('pages.1.add_achievement')}
          </button>
        </>
      )}
      {addAchievements === 'No' && typingComplete && (
        <p className="pt-4">{pageTwoTranslations.results.ok_result}</p>
      )}
    </div>
  )
}
export default UpdateYourLinkedinPageTwo
